/**
 * @fileoverview Exports Highcharts with all of our addons applied.
 *
 * Highcharts v12+ modules self-register when imported (they are no longer
 * factory functions that must be called with the Highcharts instance).
 * We use require() instead of import to control execution order -- the
 * `@braze/highcharts-rounded-corners` plugin depends on a legacy
 * `Highcharts.each` shim that must be installed before it loads.
 */

import Highcharts from "highcharts/highstock";

import "@lib/globalJquery";

import { getSafeLocale } from "./getSafeLocale";

(window as any).Highcharts = Highcharts;

if (typeof (Highcharts as any).each !== "function") {
  (Highcharts as any).each = function each<T>(
    collection: ArrayLike<T> | undefined | null,
    callback: (value: T, index: number) => void
  ): void {
    if (!collection || typeof callback !== "function") {
      return;
    }

    for (let index = 0; index < collection.length; index += 1) {
      callback(collection[index], index);
    }
  };
}

require("highcharts/highcharts-more");
require("highcharts/modules/exporting");
require("highcharts/modules/offline-exporting");
require("highcharts/modules/drilldown");
require("@braze/highcharts-rounded-corners");
require("highcharts/modules/export-data");

// Highcharts 12 otherwise resolves each chart's locale from the closest
// [lang] attribute and passes it to Intl.NumberFormat uncaught. The
// server-rendered <html lang> is whitelisted, but browser translate
// features and extensions rewrite it after load (e.g. to "auto"), which
// crashes every chart render with a RangeError.
Highcharts.setOptions({
  lang: {
    locale: getSafeLocale(
      document.documentElement.lang,
      ...(window.navigator.languages ?? []),
      window.navigator.language
    ),
  },
});

/**
 * Highcharts 12.5.0 leaks the DOM event listeners registered through
 * `SVGElement#on()`.
 *
 * `SVGElement#on()` stores each binding's unbind function in `this.onEvents`,
 * but `SVGElement#destroy()` only nulls the legacy `element.onclick` /
 * `onmouseover` / ... properties and never calls those unbinders. Measured on
 * 12.5.0: a chart with N series leaks `3 * N + 1` listener registrations per
 * create/destroy cycle (the legend items' mouseover/mouseout/click plus the
 * credits' click), and a further `3 * N` on every `chart.update()`. Series
 * trackers (`path.highcharts-tracker-line`) and point markers leak the same
 * way.
 *
 * Unbind them before delegating: `destroy()` deletes every own property at the
 * end anyway, so doing this first is a no-op for the rest of teardown.
 *
 * Remove once the upstream fix lands and we bump Highcharts.
 */
const svgElementPrototype = (Highcharts as any).SVGElement?.prototype;

if (svgElementPrototype && !svgElementPrototype.__brazeDestroyUnbindsOnEvents) {
  const originalDestroy = svgElementPrototype.destroy;

  svgElementPrototype.destroy = function destroy(this: any, ...args: unknown[]) {
    const onEvents = this.onEvents;

    if (onEvents) {
      for (const eventType of Object.keys(onEvents)) {
        const unbind = onEvents[eventType];
        if (typeof unbind === "function") {
          unbind();
        }
        delete onEvents[eventType];
      }
    }

    return originalDestroy.apply(this, args);
  };

  svgElementPrototype.__brazeDestroyUnbindsOnEvents = true;
}

export default Highcharts;
